// SCR-STAFF-1201 — ماژول‌ها (US-CFG-010): فقط Featureهای موجود در Entitlement قابل روشن و خاموش‌اند؛ الزام قانونی (BR-PLT-041) قفل
// است و خارج از پلن «نیازمند ارتقا» دارد (AC-CFG-010.1). Feature مؤثر = Entitlement × تنظیم مستأجر (AC-CFG-010.2)؛ پس از تغییر
// دسترسی پوسته (me/access) بازخوانی می‌شود تا منو و نشان‌ها به‌روز شوند. خاموش کردن داده‌ای حذف نمی‌کند.
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { unwrap, type ModuleStateView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, JalaliDate, ListPage, UpgradeBadge, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { WriteNotice } from '../settings/common';
import { cfgKeys } from './common';

export const SCR_STAFF_1201 = 'SCR-STAFF-1201';

export function ModulesPage() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cfg.modules.manage');

  const modules = useQuery({
    queryKey: cfgKeys.modules(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/configuration/modules')),
  });
  const toggle = useMutation({
    mutationFn: ({ featureKey, enabled }: { featureKey: string; enabled: boolean }) =>
      unwrap(
        tenantApi.PUT('/api/v1/configuration/modules/{featureKey}', {
          params: { path: { featureKey } },
          body: { enabled },
        }),
      ),
    onSuccess: async (result) => {
      queryClient.setQueryData(cfgKeys.modules(session?.tenantId), result);
      // Feature مؤثر در me/access (منو، UpgradeBadge و RequireAccess).
      await queryClient.invalidateQueries({ queryKey: ['identity', 'me', 'access'] });
    },
  });

  const columns: DataTableColumn<ModuleStateView>[] = [
    {
      id: 'feature',
      headerKey: 'cfg:modules.feature',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{t(`console:module.${row.module.toLowerCase()}`, { defaultValue: row.module })}</span>
          <bdi dir="ltr" style={{ fontSize: '0.75rem' }}>
            {row.featureKey}
          </bdi>
        </Stack>
      ),
    },
    {
      id: 'state',
      headerKey: 'cfg:modules.state',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={row.enabled}
            onChange={(event) => toggle.mutate({ featureKey: row.featureKey, enabled: event.target.checked })}
            disabled={!row.canToggle || !write.canWrite || toggle.isPending}
            slotProps={{ input: { 'aria-label': t('cfg:modules.toggle', { name: row.featureKey }) } }}
            data-testid={`module-toggle-${row.featureKey}`}
          />
          {row.isRegulatory && (
            <Chip
              size="small"
              icon={<LockOutlinedIcon />}
              label={t('cfg:modules.regulatory')}
              data-testid={`module-regulatory-${row.featureKey}`}
            />
          )}
          {!row.inEntitlement && <UpgradeBadge feature={row.featureKey} to={SUBSCRIPTION_PATH} />}
        </Stack>
      ),
    },
    {
      id: 'changedAt',
      headerKey: 'cfg:modules.changed_at',
      cell: (row) =>
        row.changedAt ? <JalaliDate value={row.changedAt} withTime /> : t('common:states.none'),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1201}
      titleKey="cfg:modules.title"
      subtitleKey="cfg:modules.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Alert severity="info">{t('cfg:modules.no_data_loss')}</Alert>
          {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="cfg:modules.caption"
        columns={columns}
        rows={modules.data?.items}
        getRowId={(row) => row.featureKey}
        isLoading={modules.isPending}
        error={modules.error}
        onRetry={() => void modules.refetch()}
        emptyTitleKey="cfg:modules.empty"
      />
    </ListPage>
  );
}
